import { Card, CardHeader, Stack, Button, Divider, Box, Backdrop, CircularProgress, MenuItem, Select, Typography } from "@mui/material";
import { crudGetList, ReduxState, useCreateContext, useListContext } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Scrollbar from "../Scrollbar";
import { TableOwnershipDto, EditTablesOwnershipInput, DataOwnerGroupResponseDto } from "@/gen/api/governance";
import { RecPatch } from "@/dataProvider/tools";
import { Datagrid } from "../list/datagrid";
import { FunctionField, TextField } from "../field";
import { useTablesOwnershipContext } from "./useTablesOwnershipContext";
import Create from "@/controller/Create";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { useDispatch, useSelector } from "react-redux";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { EDIT_DATA_OWNERSHIP } from "@/constants";
import { disabledColor } from "@/utils/a11y";


const TablesOwnershipList: FC = () => {
  const dispatch = useDispatch()
  const refreshDbList = useCallback(() => {
    dispatch(crudGetList('governance/data-ownership', pages(), sorts(), {}))
  }, [dispatch])

  const handlers = useDefaultSavedHandlers("Edit Data Ownership")
  handlers.onSuccess.merge(refreshDbList)

  return (
    <Create resource="govrnc-data-ownership" basePath="" {...handlers}>
      <TablesOwnershipListBody />
    </Create>
  )
}

const TablesOwnershipListBody: FC = () => {
  const hasWritePermit = useHasPermit([EDIT_DATA_OWNERSHIP])
  const listCtx = useListContext<TableOwnershipDto & RecPatch>()
  const { databaseId, getCurrOwnerId, setCurrOwnerId, editsCount, discardAllEdits, getStaged, bulkAssignOwnerGroup } = useTablesOwnershipContext()

  const { saving } = useCreateContext()
  const save = useSimpleSaveAction()
  const { refetch, selectedIds } = listCtx

  const saveOwnershipEdits = () => {
    if (!hasWritePermit) { return }
    const staged = getStaged()
    const payload: EditTablesOwnershipInput = {
      databaseId,
      list: [
        ...Object.keys(staged.deletes).map(tableId => ({ tableId: +tableId })),
        ...Object.keys(staged.edits).map(tableId => ({ tableId: +tableId, ownerGroupId: staged.edits[+tableId] })),
      ]
    }
    save(payload, {
      onSuccess: () => { 
        refetch() 
        discardAllEdits()
      }
    })
  }

  const ownerGroupData = useSelector<ReduxState, any>(store => store.admin.resources['settings/governance/data-owner-group']?.data)
  const ownerGroups = useMemo(() => (
    Object.values(ownerGroupData || {}) as DataOwnerGroupResponseDto[]
  ).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)
  , [ownerGroupData])

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(crudGetList('settings/governance/data-owner-group', pages(), sorts(), {}))
  }, [dispatch])

  const stagedRef = useRef<ReturnType<typeof getStaged>>({} as any)
  stagedRef.current = getStaged()
  const isRowSelectable = useCallback((r: TableOwnershipDto&RecPatch, isSelectAllContext?: boolean) => {
    if (!hasWritePermit) { return false }
    if (!isSelectAllContext) return true
    const staged = stagedRef.current
    return !r.ownerGroupId && !(staged.deletes[r.tableId || -1] || staged.edits[r.tableId || -1])
  }, [hasWritePermit])

  const [bulkTargetGroupId, setBulkTargetGroupId] = useState<number|''>('')

  return (
    <Card>
      <CardHeader 
        title="Table & Onwer Group" 
        action={
          hasWritePermit && editsCount > 0 &&

          <Stack direction="row" spacing={2}>
            <Button
              disabled={saving}
              variant="outlined"
              onClick={discardAllEdits}
            >
              Cancel
            </Button>
            <Button
              id="save-btn"
              disabled={saving}
              variant="contained"
              onClick={() => saveOwnershipEdits()}
            >
              Save
            </Button>
            <Divider sx={{ borderLeftWidth: '1px' }} />
            <Typography 
              id="edit-counts"
              variant="caption" 
              component="span"
              sx={{
                alignSelf: 'center',
                backgroundColor: theme => theme.palette.success.main,
                color: theme => theme.palette.success.contrastText,
                px: 0.5,
                borderRadius: '12px',
                fontWeight: '700',
              }}
            >
              +{editsCount}
              <span className="sr-only">edits</span>
            </Typography>
          </Stack>
        }
      />
      <Divider />
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150, position: 'relative' }}>
          {hasWritePermit && selectedIds?.length > 0 &&
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'background.paper',
                position: 'absolute',
                width: 'calc(100% - 64px)',
                height: 56,
                left: 52,
                pl: 1.5,
                zIndex: 3,
                '& button': { mr: 2 }
              }}
            >
              <Box id="bulk-assign-box" sx={{ display: 'flex', alignItems: 'center', }}>
                <Typography variant="subtitle2" sx={{ mr: 1 }}>Assign to Owner Group:</Typography>
                <Select
                  value={bulkTargetGroupId}
                  onChange={e => 
                    setBulkTargetGroupId(e.target.value ? +e.target.value : '')}
                  size="small"
                  sx={{ minWidth: 120, maxWidth: 360 }}
                  id="bulk-assign-select"
                  inputProps={{ labelId: 'bulk-assign-box' }}
                >
                  <MenuItem value={""}>
                    <Typography variant="body2" color="action.disabled">[ None ]</Typography>
                  </MenuItem>
                  {ownerGroups.map(r => (
                    <MenuItem key={r.ownerGroupId} value={r.ownerGroupId as any}>
                      <Typography variant="body1" sx={{ maxWidth: 360, wordBreak: 'keep-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', }}>
                        {r.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  bulkAssignOwnerGroup(bulkTargetGroupId ? bulkTargetGroupId : undefined)
                  setBulkTargetGroupId('')
                }}
                sx={{ ml: 2 }}
              >
                Go
              </Button>
            </Box>
          }
          <Datagrid 
            hasBulkActions={hasWritePermit} 
            sx={{ 
              '& th' : { py: 2 },
              '& td' : { py: 2 },
              '& td.column-tableName, & td.column-tableSchema': { width: 280 },
              ...(!hasWritePermit && {
                '& div.Mui-disabled': disabledColor,
              })
            }}
            isRowSelectable={isRowSelectable}
            reversedIndeterminateStateMode={true}
            {...listCtx}
          >
            <TextField source="tableName" label="Table Name" sortable={false} className="table-name" />
            <TextField source="tableSchema" label="Schema" sortable={false} className="table-schema" />
            <FunctionField 
              source="" 
              label="Owner Group" 
              sortable={false} 
              render={(d?:TableOwnershipDto & RecPatch) => (
                <Select
                  disabled={!hasWritePermit}
                  value={getCurrOwnerId(d?.tableId || -1)}
                  onChange={e => 
                    setCurrOwnerId(d?.tableId || -1, 
                      e.target.value === '' ? undefined : (e.target.value as number))}
                  size="small"
                  sx={{ minWidth: 80, maxWidth: 360 }}
                >
                  <MenuItem value={""}>
                    <Typography variant="body2" color="action.disabled">[ None ]</Typography>
                  </MenuItem>
                  {ownerGroups.map(r => (
                    <MenuItem key={r.ownerGroupId} value={r.ownerGroupId as any}>
                      <Typography variant="body1" sx={{ maxWidth: 360, wordBreak: 'keep-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', }}>
                        {r.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Datagrid>
        </Box>
      </Scrollbar>
      <Backdrop open={listCtx.loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    </Card>
  )
}

export default TablesOwnershipList;
